'use client'

import { type ReactNode, useEffect, useState } from 'react'
import Link from 'next/link'
import { Download, Heart, MapPin, MapPinned } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ServiceImageCarousel } from '@/components/home/ServiceImageCarousel'
import { ServiceAddressesBody } from '@/components/home/ServiceAddressesBody'
import {
  serviceCarouselImageUrls,
  serviceDetailPath,
  type MarketplaceService,
} from '@/lib/marketplace-services'
import { ServiceContactItems, ServiceSocialItems } from '@/components/home/ServiceContactSocialItems'
import { useFavoriteServices } from '@/hooks/useFavoriteServices'
import { cn } from '@/lib/utils'

export function ServiceQuickPreview({
  service,
  trigger,
}: {
  service: MarketplaceService
  trigger: ReactNode
}) {
  const detailPath = serviceDetailPath(service)
  const { toggleFavorite, isFavorite } = useFavoriteServices()
  const active = isFavorite(service.id)
  const [isDescExpanded, setIsDescExpanded] = useState(false)

  useEffect(() => {
    setIsDescExpanded(false)
  }, [service.id])
  const locationLabels = service.locations
    .map((location) =>
      location.address ||
      [location.city, location.locationName]
        .map((part) => (part ?? '').trim())
        .filter(Boolean)
        .join(', ')
    )
    .map((label) => label.trim())
    .filter(Boolean)
  const fallbackLocationLabel =
    [service.locations[0]?.city, service.locations[0]?.locationName]
      .map((part) => (part ?? '').trim())
      .filter(Boolean)
      .join(', ')

  const hasDescription =
    Boolean(service.descriptionHtml) || (service.descriptionBlocks?.length ?? 0) > 0
  const hasAddressesRich =
    Boolean(service.addressesHtml) || (service.addressesBlocks?.length ?? 0) > 0
  const fallbackPlain =
    !hasDescription &&
    !hasAddressesRich &&
    (service.descriptionExcerpt || service.addressesExcerpt)

  return (
    <Sheet>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[560px]"
      >
        <div className="px-4 md:px-7 py-4 pt-7">
          <p className="text-lg font-semibold leading-none text-foreground">
            Brzi pregled usluge
          </p>
        </div>

        <div className="flex flex-1 flex-col gap-4 px-4 pb-7 md:px-7">
          <div className="relative overflow-hidden rounded-[24px] border border-border/70 transition-all duration-300 ease-out hover:shadow-md">
            <ServiceImageCarousel
              images={serviceCarouselImageUrls(service)}
              alt={service.title}
              maxImages={5}
              largeDots
              className="h-[240px] w-full sm:h-[240px]"
            />

            {service.categories[0] && (
              <span className="absolute left-4 top-4 rounded-full bg-white/95 px-4 py-1.5 text-xs font-semibold text-[#5f7ea5]">
                {service.categories[0].name}
              </span>
            )}
            <button
              type="button"
              aria-label={active ? "Ukloni iz omiljenih" : "Dodaj u omiljene"}
              onClick={() => toggleFavorite(service)}
              className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm transition-all duration-200 ease-out hover:scale-[1.03] hover:bg-muted"
            >
              <Heart
                className={cn("h-5 w-5 text-red-500", active && "fill-current")}
              />
            </button>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold leading-[1.5] text-xl text-black dark:text-white">
              {service.title}
            </h3>

            {(locationLabels.length > 0 || fallbackLocationLabel) && (
              <div className="space-y-2">
                {locationLabels.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {locationLabels.map((label, idx) => (
                      <div
                        key={`${label}-${idx}`}
                        className="inline-flex items-center gap-3 rounded-xl bg-[#F9F9FD] px-4 py-2 text-sm font-medium text-black transition-all duration-200 ease-out hover:-translate-y-[1px] hover:bg-[#ECECF7] dark:bg-muted/45 dark:text-white dark:hover:bg-muted/30"
                      >
                        <MapPin className="h-5 w-5 shrink-0 text-foreground/80" />
                        {label}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-3 rounded-xl bg-[#F9F9FD] px-4 py-2 text-sm font-medium text-black transition-all duration-200 ease-out hover:-translate-y-[1px] hover:bg-[#ECECF7] dark:bg-muted/45 dark:text-white dark:hover:bg-muted/30">
                    <MapPin className="h-5 w-5 shrink-0 text-foreground/80" />
                    {fallbackLocationLabel}
                  </div>
                )}

                {service.serviceAddresses.length > 0 && (
                  <ul className="space-y-1.5">
                    {service.serviceAddresses.map((address, idx) => (
                      <li key={address.id ?? `${address.addressTitle}-${idx}`}>
                        {address.addressMapUrl && (
                          <a
                            href={address.addressMapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 rounded-xl bg-[#F9F9FD] px-4 py-2 text-sm font-medium text-black underline underline-offset-4 transition-all duration-200 hover:bg-[#ECECF7] hover:text-primary dark:bg-muted/45 dark:text-white dark:hover:bg-muted/30 dark:hover:text-white/80"
                          >
                            <MapPinned className="h-5 w-5 shrink-0 text-foreground/80" />
                            {address.addressTitle}
                          </a>
                        ) }
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {(hasDescription || hasAddressesRich || fallbackPlain) && (
            <div className="space-y-2 border-t border-border/70 pt-4">
              <div className="relative">
                <div
                  className={cn(
                    "overflow-hidden",
                    !isDescExpanded && "max-h-[280px]",
                  )}
                  style={isDescExpanded ? undefined : { WebkitMaskImage: 'none' }}
                >
                  {hasDescription && (
                    <section className="prose prose-neutral max-w-none text-sm dark:prose-invert sm:text-base [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:my-3 [&_li]:my-1">
                      <ServiceAddressesBody
                        html={service.descriptionHtml}
                        blocks={service.descriptionBlocks}
                      />
                    </section>
                  )}
                  {hasAddressesRich && (
                    <section className="prose prose-neutral max-w-none text-sm dark:prose-invert sm:text-base [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:my-3 [&_li]:my-1">
                      <ServiceAddressesBody
                        html={service.addressesHtml}
                        blocks={service.addressesBlocks}
                      />
                    </section>
                  )}
                  {fallbackPlain && (
                    <p className="text-[15px] leading-relaxed text-foreground/90 sm:text-base">
                      {service.descriptionExcerpt || service.addressesExcerpt}
                    </p>
                  )}
                </div>

                {!isDescExpanded && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background via-background/80 to-transparent dark:from-background dark:via-background/80" />
                )}
              </div>

              {!isDescExpanded && (
                <button
                  type="button"
                  onClick={() => setIsDescExpanded(true)}
                  className="inline-flex items-center rounded-full bg-muted px-4 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/70 dark:bg-muted/60 dark:text-foreground dark:hover:bg-muted/40"
                >
                  Prikaži više
                </button>
              )}
            </div>
          )}

          {(service.phoneNumbers?.length > 0 ||
            service.contactEmail ||
            service.whatsappNumber ||
            service.viberNumber ||
            service.websiteUrl) && (
            <section className="space-y-3 border-t border-border/70 pt-5">
              <h4 className="text-xs font-normal leading-[1.5] text-[#3E4256] dark:text-white">
                Kontakt
              </h4>
              <ServiceContactItems
                phoneNumbers={service.phoneNumbers}
                contactEmail={service.contactEmail}
                whatsappNumber={service.whatsappNumber}
                viberNumber={service.viberNumber}
                websiteUrl={service.websiteUrl}
                className="grid gap-2 grid-cols-3"
              />
            </section>
          )}

          {(service.instagramUrl ||
            service.facebookUrl) && (
            <section className="space-y-3 border-t border-border/70 pt-4">
              <h4 className="text-xs font-normal leading-[1.5] text-[#3E4256] dark:text-white">
                Društvene mreže
              </h4>
              <ServiceSocialItems
                instagramUrl={service.instagramUrl}
                facebookUrl={service.facebookUrl}
                className="flex items-center gap-3"
              />
            </section>
          )}

          {service.additionalOptionalFileUrl && (
            <div className="border-t border-border/70 pt-4">
              <Button variant="secondary" className="w-full" asChild>
                <a
                  href={service.additionalOptionalFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                  {service.additionalOptionalFileButtonText ?? service.additionalOptionalFileName ?? "Preuzmi prilog"}
                </a>
              </Button>
            </div>
          )}

          <Button
            className="h-[46px] w-full rounded-md transition-all duration-200 ease-out hover:-translate-y-[1px] hover:bg-primary/90"
            asChild
          >
            <Link
              href={detailPath}
              className="text-sm py-4 font-semibold leading-[1.5]"
            >
              Pogledaj detaljnije
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
